import { Component, EventEmitter, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { from, of } from 'rxjs';
import { catchError, concatMap, finalize, mergeMap } from 'rxjs/operators';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { ProgressionDialogData } from 'src/app/shared/components/progression-dialog/progression-dialog-data.model';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { TaskService } from 'src/app/shared/services/task.service';
import { AfterSendActionType, LogAnswerRequest } from 'src/app/task-process/shared/model';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { TaskProcessService } from 'src/app/task-process/shared/task-process.service';
import { Task } from 'src/app/tasks/shared/models';

import { TaskListConfiguration } from '../../shared/models';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { MassActionsService } from '../mass-actions.service';

@Component({
  selector: 'app-mass-answer-dialog',
  templateUrl: './mass-answer-dialog.component.html',
  styleUrls: ['./mass-answer-dialog.component.scss'],
})
export class MassAnswerDialogComponent implements OnInit {
  site: SiteV2;
  editorControl = new FormControl([], Validators.required);
  isPrivate = true;

  taskProcessContext: TaskProcessContext;
  nbMessages = 0;

  showProgressionDialog = false;
  progressionDialogData: ProgressionDialogData = {
    done: 0,
    total: 0,
    progressionPercentage: 0,
    isLoading: false,
    hasError: false,
    updateProgression: new EventEmitter(),
    title: this.basicService.translate('inbox/task-list/mass-actions/answer/progress/title'),
    progressionText: this.basicService.translate('inbox/task-list/mass-actions/answer/in-progress'),
    finishedText: this.basicService.translate('inbox/task-list/mass-actions/answer/finished'),
    errorText: this.basicService.translate('inbox/task-list/mass-actions/answer/error'),
  };

  isConfirmationVisible = false;
  isConfirmationChecked = false;

  @ViewChild('progressDialogTplRef') progressDialogTplRef: TemplateRef<any>;

  constructor(
    private basicService: BasicService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ExportDialogComponent>,
    private massActionsService: MassActionsService,
    private sitesService: SitesService,
    private taskProcessService: TaskProcessService,
    private taskService: TaskService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      taskListConfiguration: TaskListConfiguration;
      tasksById: { [key: number]: Task };
    }
  ) {}

  ngOnInit(): void {
    // ensure tasks selected are all of the same channel or same type in mass-actions.component
    const siteOfFirstMessage = this.data.tasksById[this.massActionsService.idsSelection[0]];
    this.site = this.sitesService.sitesV2Map.get(siteOfFirstMessage.site.id);
    this.isPrivate = !!siteOfFirstMessage.message.is_private;

    this.nbMessages = this.massActionsService.idsSelection.length;
  }

  confirm() {
    if (!this.isConfirmationVisible) {
      this.isConfirmationVisible = true;
    } else {
      this.send();
    }
  }

  close() {
    this.dialogRef.close(true);
  }

  cancel() {
    this.dialogRef.close();
  }

  private send() {
    this.progressionDialogData.progressionPercentage = 0;
    this.progressionDialogData.done = 0;
    this.progressionDialogData.total = this.nbMessages;
    this.progressionDialogData.isLoading = true;
    this.progressionDialogData.hasError = false;
    this.showProgressionDialog = true;
    this.close();

    from(this.massActionsService.idsSelection)
      .pipe(
        concatMap(taskId => {
          const requestData = this.buildLogAnswserRequest(taskId);
          const afterSendAction = { type: AfterSendActionType.None }; // like onDesktopSendClicked() in answer-editor.component.ts

          // like sendAnswer() in task-process-page.component.ts
          return this.taskService.aquireTaskFromAnyState(requestData.taskId).pipe(
            mergeMap(() => this.taskProcessService.logAnswerThenExecuteAnswer(requestData, afterSendAction)),

            // catchError + return of(null) do not interrupt others calls if error
            catchError(() => {
              this.progressionDialogData.hasError = true;
              return of(null);
            })
          );
        }),

        finalize(() => {
          this.progressionDialogData.isLoading = false;

          if (this.progressionDialogData.hasError) {
            // auto hide error displayed by catchAlertAndThrowHttpError, because there could be plenty
            setTimeout(() => {
              const interval = setInterval(async () => {
                const toast = await this.basicService.toastController.getTop();

                if (toast) {
                  this.basicService.toastController.dismiss();
                } else {
                  clearInterval(interval);
                }
              }, 300);
            }, 3000);
          }
        })
      )
      .subscribe({
        next: () => {
          this.progressionDialogData.done++;
          this.progressionDialogData.updateProgression.emit();
        },
      });
  }

  /**
   * Mimic buildLogAnswserRequest in task-process-context.class.ts
   */
  private buildLogAnswserRequest(taskId: number): LogAnswerRequest {
    const currentTask = this.data.tasksById[taskId];

    return {
      taskId: taskId,
      replyAs: {
        id: currentTask.site.id,
      },
      replyTo: currentTask.message.from,
      replyToUserIds: [currentTask.message.from.id],
      isPrivate: !!currentTask.message.is_private,
      answers: this.editorControl.value,
    };
  }

  closeProgressionDialog() {
    this.showProgressionDialog = false;
  }
}
